import { Printer, ChevronLeft, Award, Loader2, BookOpen, Eye, FileText } from 'lucide-react';
import { StudentReportCard, getStudentReportData } from './ReportCards';
import type { PracticeExam } from '../../types/practice-exam';
import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';
import WebStudentReportCard from './components/WebStudentReportCard';

export default function MyReportCards() {
    const { user, systemConfig } = useAuth();
    const { showAlert } = useModal();
    const [loading, setLoading] = useState(false);
    const [exams, setExams] = useState<PracticeExam[]>([]);
    const [selectedExam, setSelectedExam] = useState<PracticeExam | null>(null);
    const [reportData, setReportData] = useState<any>(null);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [totalStudents, setTotalStudents] = useState(0);
    const [previewMode, setPreviewMode] = useState<'web' | 'a4'>('web');

    useEffect(() => {
        fetchExams();
    }, []);

    const fetchExams = async () => {
        try {
            setLoading(true);
            const res = await ApiService.getUserPracticeExams({ limit: 100 });
            setExams(res.rows || []);
        } catch (error) {
            console.error(error);
            showAlert({ title: 'Hata', message: 'Denemeler yüklenirken hata oluştu', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExamClick = async (exam: PracticeExam) => {
        if (!user) return;

        try {
            setSelectedExam(exam);
            setResultsLoading(true);
            setPreviewMode('web');

            const res = await ApiService.getMyPracticeExamResult(exam.id);
            const myResult = (res as any)?.result || null;
            const overallStats = (res as any)?.overallStats || {};
            const totalStudentsFromStats = Number(overallStats?.totalStudents) || 0;
            setTotalStudents(totalStudentsFromStats);

            if (myResult) {
                const examMetadata = {
                    bookletMapping: (res as any)?.bookletMapping || {},
                    testNames: (res as any)?.testNames || {},
                    testAverages: (res as any)?.testAverages || {},
                    overallStats,
                    levelType: exam.levelType,
                    yksMode: exam.yksMode,
                };

                const data = {
                    ...getStudentReportData(myResult, examMetadata),
                    examId: exam.id,
                    additionalInfo: (res as any)?.additionalInfo || null,
                };
                setReportData(data);
            } else {
                setReportData(null);
                showAlert({ title: 'Bilgi', message: 'Bu deneme için sonucunuz bulunamadı.', type: 'info' });
            };
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error.message || 'Sonuç yüklenirken hata oluştu', type: 'error' });
            setSelectedExam(null);
        } finally {
            setResultsLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedExam(null);
        setReportData(null);
        setPreviewMode('web');
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="flex h-full flex-col space-y-4 p-4 sm:space-y-6 sm:p-6 overflow-y-auto">
            <div className="no-print flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3 sm:items-center sm:gap-4">
                    {selectedExam && (
                        <button
                            onClick={handleBack}
                            className="rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100"
                        >
                            <ChevronLeft size={22} />
                        </button>
                    )}
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold leading-tight text-gray-800 sm:text-2xl">
                            {selectedExam ? selectedExam.name : 'Karnelerim'}
                        </h2>
                        <p className="mt-1 text-sm leading-6 text-gray-500 sm:text-base">
                            {selectedExam ? 'Sınav Sonuç Karnesi ve Detaylı Analiz' : 'Girdiğiniz sınavların karnelerini buradan görüntüleyebilirsiniz.'}
                        </p>
                    </div>
                </div>
                {selectedExam && reportData && (
                    <div className="flex w-full gap-2 sm:w-auto">
                        <button
                            onClick={() => setPreviewMode(previewMode === 'web' ? 'a4' : 'web')}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-all sm:flex-initial sm:px-5 ${previewMode === 'a4'
                                    ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                            title={previewMode === 'a4' ? 'Sistem görünümüne dön' : 'A4 çıktı önizlemesi'}
                        >
                            {previewMode === 'a4' ? <FileText size={16} /> : <Eye size={16} />}
                            <span>{previewMode === 'a4' ? 'Web Görünümü' : 'A4 Önizleme'}</span>
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 shadow-sm sm:flex-initial sm:px-5"
                        >
                            <Printer size={16} />
                            <span>Yazdır / İndir</span>
                        </button>
                    </div>
                )}
            </div>

            {!selectedExam ? (
                loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : exams.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-12 text-center border-2 border-dashed border-gray-200">
                        <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">Henüz Sınav Kaydı Yok</h3>
                        <p className="text-gray-500 mt-1">Sistemde açıklanmış sınav sonucu bulunmamaktadır.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {exams.map((exam) => (
                            <div
                                key={exam.id}
                                onClick={() => handleExamClick(exam)}
                                className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Award className="w-16 h-16 text-blue-600 transform rotate-12" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 line-clamp-2" title={exam.name}>
                                    {exam.name}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                        {new Date(exam.date || '').toLocaleDateString('tr-TR')}
                                    </span>
                                </p>
                                <div className="flex items-center text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform">
                                    Karneyi Görüntüle
                                    <ChevronLeft className="w-4 rotate-180 ml-1" />
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : resultsLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            ) : reportData ? (
                <>
                    {previewMode === 'web' ? (
                        <WebStudentReportCard
                            data={reportData}
                            examName={selectedExam?.name}
                            companyLogo={systemConfig?.companyLogo || undefined}
                            companyName={systemConfig?.companyName || undefined}
                            totalStudents={totalStudents}
                        />
                    ) : (
                        <div className="report-preview-shell">
                            <div className="report-preview-paper print:shadow-none print:w-full">
                                <StudentReportCard
                                    data={reportData}
                                    examName={selectedExam?.name}
                                    companyLogo={systemConfig?.companyLogo || undefined}
                                    companyName={systemConfig?.companyName || undefined}
                                    totalStudents={totalStudents}
                                />
                            </div>
                        </div>
                    )}

                    <div className="hidden print:block print:fixed print:inset-0 print:bg-white print:z-[99999]">
                        <div className="print-page-break w-[210mm] h-[297mm] relative mx-auto bg-white p-8 box-border">
                            <StudentReportCard
                                data={reportData}
                                examName={selectedExam?.name}
                                isPrint
                                companyLogo={systemConfig?.companyLogo || undefined}
                                companyName={systemConfig?.companyName || undefined}
                                totalStudents={totalStudents}
                            />
                        </div>
                    </div>
                </>
            ) : null}

            <style>{`
            .report-preview-shell {
               display: flex;
               justify-content: center;
               padding: 0.5rem 0 1.5rem;
            }

            .report-preview-paper {
               width: 210mm;
               min-height: 297mm;
               padding: 5mm;
               background: white;
               border-radius: 18px;
               box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
               border: 1px solid #e5e7eb;
               flex-shrink: 0;
            }

            @media (min-width: 1024px) {
               .report-preview-shell {
                 padding: 1rem 0 5.5rem;
               }

               .report-preview-paper {
                 transform: scale(1.12);
                 transform-origin: top center;
               }
            }

            @media (max-width: 767px) {
               .report-preview-shell {
                 justify-content: flex-start;
                 padding: 0.25rem 0 1rem;
               }

               .report-preview-paper {
                 border-radius: 14px;
                 box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
               }
            }

            @media print {
               @page {
                 size: A4;
                 margin: 0;
               }

               html, body {
                 background: white !important;
                 -webkit-print-color-adjust: exact !important;
                 print-color-adjust: exact !important;
                 margin: 0 !important;
                 padding: 0 !important;
                 overflow: visible !important;
                 height: auto !important;
               }

               #root, #app, .App {
                 display: block !important;
                 margin: 0 !important;
                 padding: 0 !important;
                 width: 100% !important;
               }

               aside,
               [class*="sidebar"],
               [class*="Sidebar"],
               [class*="left-0"][class*="fixed"],
               [class*="bg-white"][class*="shadow-lg"][class*="flex-col"] {
                 display: none !important;
                 width: 0 !important;
               }

               header,
               nav,
               [class*="navbar"],
               [class*="Navbar"] {
                 display: none !important;
               }

               .print-page-break {
                 page-break-after: always;
                 break-after: page;
                 padding: 10mm !important;
                 box-sizing: border-box !important;
               }

               .report-preview-shell,
               .report-preview-paper {
                 transform: none !important;
                 box-shadow: none !important;
                 border: 0 !important;
                 padding: 0 !important;
               }

               .no-print {
                 display: none !important;
               }

               .print-area table {
                 border-collapse: collapse !important;
                 border-spacing: 0 !important;
               }

               .print-area .border-l { border-left: 1px solid #000 !important; }
               .print-area .border-r { border-right: 1px solid #000 !important; }
               .print-area .border-r-2 { border-right: 2px solid #000 !important; }
               .print-area .border-t { border-top: 1px solid #000 !important; }
               .print-area .border-b { border-bottom: 1px solid #000 !important; }
               .print-area .border-black { border-color: #000 !important; }
               .print-area .border { border: 1px solid #000 !important; }
            }
         `}</style>
        </div>
    );
}
